import React, { useEffect, useMemo, useState } from "react";
import { publicOrigin } from "@/lib/landerDomain";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, TrendUp, CheckCircle, XCircle, Lightning, Flask, CurrencyDollar, ChartBar, CaretDown, CaretRight, BookOpen, Globe, Copy, Warning, Crosshair } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { RotatorLinks } from "@/components/vendor/RotatorLinks";
import { VendorMarketplaceStats } from "@/components/vendor/VendorMarketplaceStats";

// Vendor-only read-only dashboard (iter-30.6 + 30.7).
//   - Today submitted + accepted vs per-reason rejected.
//   - Weekly earned = accepted × the platform's cost-per-state to this vendor
//     (NEVER the buyer's sell price — vendors don't see markup).
//   - Live vs sandbox split so the vendor can confirm which key they're using.
//   - Per-state table showing count + unit cost + earnings.

function pct(n) { return `${Math.round(Number(n || 0) * 100)}%`; }
function usd(n) { return `$${Number(n || 0).toFixed(2)}`; }
function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

const STATUS_STYLE = {
  accepted:  "bg-emerald-100 text-emerald-900 border-emerald-800",
  validated: "bg-emerald-100 text-emerald-900 border-emerald-800",
  delivered: "bg-emerald-100 text-emerald-900 border-emerald-800",
  rejected_invalid: "bg-rose-100 text-rose-900 border-rose-800",
  duplicate: "bg-amber-100 text-amber-900 border-amber-800",
  capped:    "bg-amber-100 text-amber-900 border-amber-800",
  paused:    "bg-zinc-200 text-zinc-800 border-zinc-800",
};

// Iter-30.7: per-rejection-reason chips on the vendor dashboard so
// they know exactly what to fix in their feed.
const REJECT_REASONS = [
  { key: "invalid",   label: "Invalid",   hint: "PI validation failed — required fields missing or bad data",
    color: "bg-rose-100 text-rose-900 border-rose-800" },
  { key: "duplicate", label: "Duplicate", hint: "Same consumer already exists in the system",
    color: "bg-amber-100 text-amber-900 border-amber-800" },
  { key: "capped",    label: "Capped",    hint: "The buyer already hit their volume cap for the day/week/month",
    color: "bg-orange-100 text-orange-900 border-orange-800" },
  { key: "paused",    label: "Paused",    hint: "Delivery destination or campaign was paused when this lead came in",
    color: "bg-zinc-200 text-zinc-800 border-zinc-800" },
  { key: "other",     label: "Other",     hint: "Any other non-accepted status",
    color: "bg-zinc-100 text-zinc-700 border-zinc-600" },
];

// Human-readable label for each landing_page slug.
const LP_LABELS = {
  "auto-accident": "Auto Accident",
  "truck-accident": "Truck Accident",
  "rideshare-accident": "Rideshare (Uber/Lyft)",
  "motorcycle-accident": "Motorcycle",
  "personal-injury": "Personal Injury (all-in-one)",
  "free-case-review": "Free Case Review (all-in-one)",
};

/**
 * Iter-33: A/B head-to-head — renders ONLY when both PI variants
 * (`personal-injury` + `free-case-review`) have at least one submission in
 * the current window. Otherwise renders nothing so the section doesn't add
 * noise for vendors who aren't running the split test.
 */
function AbHeadToHead({ pages }) {
  const pi = pages.find((p) => p.landing_page === "personal-injury");
  const fcr = pages.find((p) => p.landing_page === "free-case-review");
  if (!pi || !fcr || (pi.submitted === 0 && fcr.submitted === 0)) return null;

  const winner =
    pi.acceptance_rate > fcr.acceptance_rate
      ? "personal-injury"
      : fcr.acceptance_rate > pi.acceptance_rate
        ? "free-case-review"
        : null;

  const cell = (p, key) => {
    const isWinner = winner === key;
    return (
      <div
        className={`border-2 border-black p-4 ${isWinner ? "bg-emerald-100" : "bg-white"}`}
        data-testid={`ab-cell-${key}`}
      >
        <div className="flex items-center gap-2">
          <div className="font-chivo font-black text-lg uppercase tracking-tighter">
            {LP_LABELS[key]}
          </div>
          {isWinner && (
            <span
              className="bg-black text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5"
              data-testid={`ab-winner-${key}`}
            >
              WINNING
            </span>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 font-mono">/lp/{key}</div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
          <div>
            <div className="label-mono">Submitted</div>
            <div className="font-chivo font-black text-xl">{p.submitted}</div>
          </div>
          <div>
            <div className="label-mono">Accepted</div>
            <div className="font-chivo font-black text-xl text-emerald-800">{p.accepted}</div>
          </div>
          <div>
            <div className="label-mono">Rate</div>
            <div className="font-chivo font-black text-xl">{pct(p.acceptance_rate)}</div>
          </div>
        </div>
        <div className="mt-2 text-[11px] font-mono">
          Earned: <b>${p.earned.toFixed(2)}</b>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-6" data-testid="ab-head-to-head">
      <div className="label-mono mb-2 flex items-center gap-2">
        A/B — variants head-to-head
        {winner && (
          <span className="text-[10px] normal-case text-zinc-500">
            (delta:{" "}
            {Math.round(Math.abs(pi.acceptance_rate - fcr.acceptance_rate) * 100)}pp)
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cell(pi, "personal-injury")}
        {cell(fcr, "free-case-review")}
      </div>
    </div>
  );
}

/**
 * Iter-32: Traffic Sources — landing-page + sub_id conversion breakdown.
 *
 * A vendor drives clicks through different landing pages and tags each ad
 * with a `sub=…` id. This collapsible section shows them exactly which
 * page + sub_id combo is turning into accepted (paid) leads so they can
 * double down.
 */
function TrafficSources() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    api.get(`/vendor/traffic-sources?days=${days}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (alive) setErr(e.response?.data?.detail || "Failed to load traffic sources");
      });
    return () => { alive = false; };
  }, [days]);

  const maxSubmitted = useMemo(() => {
    if (!data?.landing_pages?.length) return 1;
    return Math.max(...data.landing_pages.map((p) => p.submitted), 1);
  }, [data]);

  return (
    <div className="brut-card mb-8" data-testid="traffic-sources">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="traffic-sources-toggle"
        className="w-full px-5 py-3 flex items-center gap-2 bg-white border-b border-black text-left hover:bg-zinc-50"
      >
        {open
          ? <CaretDown size={14} weight="bold" />
          : <CaretRight size={14} weight="bold" />}
        <ChartBar size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Traffic sources · landing pages + sub_id
        </div>
        <div className="label-mono ml-auto flex items-center gap-3">
          {data && (
            <>
              <span>submitted: {data.totals?.submitted || 0}</span>
              <span>accepted: {data.totals?.accepted || 0}</span>
              <span className="font-bold">${(data.totals?.earned || 0).toFixed(2)}</span>
            </>
          )}
        </div>
      </button>

      {open && (
        <div className="p-5" data-testid="traffic-sources-body">
          <div className="flex items-center gap-2 mb-4">
            <label className="label-mono">window:</label>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                data-testid={`traffic-window-${d}`}
                className={`border border-black px-2 py-1 text-[10px] uppercase tracking-widest ${
                  days === d ? "bg-black text-white" : "hover:bg-zinc-100"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>

          {err && (
            <div className="text-sm text-rose-800 border border-rose-800 p-3 bg-rose-50">{err}</div>
          )}

          {!data && !err && (
            <div className="label-mono text-zinc-500">Loading…</div>
          )}

          {data && !err && (
            <>
              {/* Per-landing-page bar chart */}
              <div className="mb-6" data-testid="traffic-pages-chart">
                <div className="label-mono mb-2">Submissions by landing page</div>
                {!data.landing_pages.length && (
                  <div className="text-xs text-zinc-500 py-4">
                    No landing-page traffic in the last {days} days. Drive clicks to
                    <code className="font-mono mx-1 text-[11px] bg-zinc-100 px-1">/lp/auto-accident?aff=<i>your-src</i></code>
                    or any other vertical to see conversion here.
                  </div>
                )}
                {data.landing_pages.map((p) => {
                  const submittedPct = (p.submitted / maxSubmitted) * 100;
                  const acceptedPct = p.submitted ? (p.accepted / p.submitted) * 100 : 0;
                  return (
                    <div
                      key={p.landing_page}
                      className="grid grid-cols-[minmax(120px,180px)_1fr_auto] items-center gap-3 py-2 border-b border-zinc-100"
                      data-testid={`traffic-page-${p.landing_page}`}
                    >
                      <div className="text-xs">
                        <div className="font-bold">{LP_LABELS[p.landing_page] || p.landing_page}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">/lp/{p.landing_page}</div>
                      </div>
                      <div className="relative h-6 bg-zinc-100 border border-zinc-300 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-zinc-300"
                          style={{ width: `${submittedPct}%` }}
                          title={`${p.submitted} submitted`}
                        />
                        <div
                          className="absolute inset-y-0 left-0 bg-emerald-700"
                          style={{ width: `${(p.accepted / maxSubmitted) * 100}%` }}
                          title={`${p.accepted} accepted (${pct(acceptedPct / 100)})`}
                        />
                      </div>
                      <div className="text-right text-[11px] font-mono">
                        <div><b>{p.accepted}</b>/{p.submitted} · {pct(acceptedPct / 100)}</div>
                        <div className="text-[10px] text-zinc-500">${p.earned.toFixed(2)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Iter-33: A/B — variants head-to-head — only shown when both PI slugs have traffic */}
              <AbHeadToHead pages={data.landing_pages} />

              {/* Per-sub_id drill-down table */}
              <div data-testid="traffic-sources-table">
                <div className="label-mono mb-2">By landing page + sub_id (top 20 by earnings)</div>
                <div className="overflow-x-auto">
                <table className="w-full text-xs border border-black min-w-[560px]">
                  <thead>
                    <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                      <th className="text-left py-2 px-3">Landing page</th>
                      <th className="text-left py-2 px-3">sub_id</th>
                      <th className="text-right py-2 px-3">Submitted</th>
                      <th className="text-right py-2 px-3">Accepted</th>
                      <th className="text-right py-2 px-3">Rate</th>
                      <th className="text-right py-2 px-3">Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sources.slice(0, 20).map((s, i) => (
                      <tr
                        key={`${s.landing_page}-${s.sub_id}-${i}`}
                        className="border-b border-zinc-200"
                        data-testid={`traffic-row-${s.landing_page}-${s.sub_id}`}
                      >
                        <td className="py-2 px-3 font-mono">/{s.landing_page}</td>
                        <td className="py-2 px-3 font-mono">{s.sub_id}</td>
                        <td className="py-2 px-3 text-right font-mono">{s.submitted}</td>
                        <td className="py-2 px-3 text-right font-mono">
                          <span className="text-emerald-800">{s.accepted}</span>
                        </td>
                        <td className="py-2 px-3 text-right font-mono">{pct(s.acceptance_rate)}</td>
                        <td className="py-2 px-3 text-right font-mono font-bold">${s.earned.toFixed(2)}</td>
                      </tr>
                    ))}
                    {!data.sources.length && (
                      <tr>
                        <td colSpan={6} className="py-6 text-center label-mono">
                          No sub_id traffic yet. Add <code className="font-mono">&amp;sub=your-ad-set-id</code> to your tracking URL to slice by source.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Iter-66 — Approved Landing Pages panel.
 *
 * Renders every /lp/ slug the admin has explicitly approved for this
 * vendor via `organizations.allowed_landing_page_slugs`. For each row we
 * show a pre-filled tracking URL: `{origin}/lp/{slug}?aff={public_source_id}`
 * with a one-click Copy button so the vendor can paste it into their ad
 * platform. Any slug currently "unpublished" (admin approved before the
 * custom page went live/paused) is rendered under a warning banner so
 * the vendor knows it's approved but not currently accepting traffic.
 *
 * An empty allowlist = STRICT ZERO — the endpoint 403s every
 * affiliate-credited hit. That state gets a prominent notice so vendors
 * know to contact their account manager.
 */
function ApprovedLandingPages({ approved, publicSourceId }) {
  const [copiedSlug, setCopiedSlug] = useState(null);
  const origin = publicOrigin();
  const trackingUrl = (p) => {
    // Buyer pages link to the shared funnel (`/lp/<funnel>?b=<buyer>`).
    const [base, query] = (p.url_path || `/lp/${p.slug}`).split("?");
    const qs = [publicSourceId ? `aff=${publicSourceId}` : "", query || ""].filter(Boolean).join("&");
    return `${origin}${base}${qs ? `?${qs}` : ""}`;
  };
  const copyOne = async (p) => {
    const slug = p.slug;
    try {
      await navigator.clipboard.writeText(trackingUrl(p));
      setCopiedSlug(slug);
      toast.success("Tracking URL copied");
      setTimeout(() => setCopiedSlug((c) => (c === slug ? null : c)), 1600);
    } catch {
      toast.error("Copy failed — clipboard blocked");
    }
  };

  const live = approved.filter((p) => (p.status || "").toLowerCase() === "published");
  const stale = approved.filter((p) => (p.status || "").toLowerCase() !== "published");

  return (
    <div className="brut-card p-5 mb-8" data-testid="approved-landing-pages">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Globe size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Your approved landing pages
        </div>
        <div className="label-mono ml-auto">
          {approved.length === 0
            ? "none — contact account manager"
            : `${live.length} live · ${stale.length} paused`}
        </div>
      </div>

      {approved.length === 0 && (
        <div
          className="border-2 border-amber-700 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3"
          data-testid="approved-lps-empty"
        >
          <Warning size={16} weight="fill" className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-bold mb-1">You have no approved landing pages.</div>
            <div className="text-[12px] leading-relaxed">
              Any traffic driven with <span className="font-mono">?aff={publicSourceId || "…"}</span> will be
              rejected at intake with a <b>403</b>. Reach out to your account manager to have specific
              landing pages added to your allowlist.
            </div>
          </div>
        </div>
      )}

      {live.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="approved-lps-list">
          {live.map((p) => (
            <div
              key={p.slug}
              className="border-2 border-black bg-white p-3"
              data-testid={`approved-lp-${p.slug}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{p.name}</div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">
                    /lp/{p.slug} · <span className="uppercase tracking-widest">{p.kind}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyOne(p)}
                  data-testid={`approved-lp-copy-${p.slug}`}
                  className={`border border-black px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${copiedSlug === p.slug ? "bg-emerald-600 text-white border-emerald-700" : "hover:bg-black hover:text-white"}`}
                  title="Copy tracking URL"
                >
                  <Copy size={10} weight="bold" />
                  {copiedSlug === p.slug ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="mt-2 bg-zinc-100 border border-zinc-300 px-2 py-1 font-mono text-[10px] break-all"
                   data-testid={`approved-lp-url-${p.slug}`}>
                {trackingUrl(p)}
              </div>
            </div>
          ))}
        </div>
      )}

      {stale.length > 0 && (
        <div
          className="mt-4 border-2 border-amber-700 bg-amber-50 p-3"
          data-testid="approved-lps-stale"
        >
          <div className="label-mono text-amber-900 mb-2 flex items-center gap-1">
            <Warning size={12} weight="fill" />
            Approved but currently paused ({stale.length})
          </div>
          <div className="text-[11px] text-amber-900 mb-2 leading-relaxed">
            These slugs are still on your allowlist but the underlying landing page
            is <b>not currently live</b>. Traffic driven to them will fail at intake
            until the admin republishes the page.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {stale.map((p) => (
              <div
                key={p.slug}
                className="border border-amber-700 bg-white p-2 text-xs"
                data-testid={`approved-lp-stale-${p.slug}`}
              >
                <div className="font-bold truncate">{p.name}</div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">
                  /lp/{p.slug} · status: {p.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {approved.length > 0 && (
        <div className="mt-4 text-[10px] text-zinc-500 leading-relaxed">
          Add <span className="font-mono">&amp;sub=your-ad-id</span> to any tracking URL to slice conversion by ad
          set on the Traffic Sources card below.
        </div>
      )}
    </div>
  );
}

export default function VendorDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/vendor/dashboard")
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (alive) setErr(e.response?.data?.detail || "Failed to load dashboard");
      });
    return () => { alive = false; };
  }, []);

  const signOut = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  const stateRows = useMemo(
    () => (data?.week?.by_state || []).slice(0, 20),
    [data],
  );

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col">
        {/* Iter-71.2: keep the "End impersonation" exit on the error
            screen too — otherwise a super_admin who impersonates a
            broken vendor account is stranded with only a Sign-out. */}
        <ImpersonationBanner />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="brut-card p-8 max-w-md">
            <div className="label-mono">Access error</div>
            <div className="font-chivo font-black text-2xl uppercase tracking-tighter">
              Cannot load dashboard
            </div>
            <div className="mt-3 text-sm text-zinc-600">{err}</div>
            <button
              data-testid="vendor-signout-error"
              onClick={signOut}
              className="mt-6 border border-black px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-black hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center label-mono text-xs">
        Loading dashboard…
      </div>
    );
  }

  const today = data.today || {};
  const week = data.week || {};
  const todayBreak = today.rejected_breakdown || {};
  const weekBreak = week.rejected_breakdown || {};
  const unpricedWeek = week.unpriced_states || [];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="vendor-dashboard">
      {/* Iter-71.2: role-scoped dashboards live outside <Layout>, so we
          have to mount ImpersonationBanner locally — otherwise a super_admin
          who "Test as vendor" gets stuck with no way back to the admin view. */}
      <ImpersonationBanner />
      {/* Top bar */}
      <div className="bg-black text-white border-b-4 border-black">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 px-4 md:px-6 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">
              LeadFlow — Affiliate portal
            </div>
            <div className="font-chivo font-black text-xl md:text-2xl uppercase tracking-tighter leading-none mt-1 truncate">
              {data.affiliate?.name || "Affiliate"}
            </div>
            <div className="text-[10px] text-zinc-400 mt-1 font-mono truncate">
              signed in as {user?.email}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/postbacks"
              data-testid="vendor-postbacks-link"
              className="border border-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-2"
            >
              <Crosshair size={12} weight="bold" /> Postbacks &amp; Pixels
            </Link>
            <a
              href="/help?slug=vendor-portal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vendor-help-link"
              className="border border-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-2"
            >
              <BookOpen size={12} weight="bold" /> How do I use this?
            </a>
            <button
              data-testid="vendor-signout"
              onClick={signOut}
              className="border border-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-2"
            >
              <SignOut size={12} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Metric strip: submitted / accepted / rejected / delivered / EARNED */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="brut-card p-4" data-testid="metric-today-submitted">
            <div className="label-mono">Today · submitted</div>
            <div className="font-chivo font-black text-3xl mt-1">{today.submitted || 0}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Last 24 hours UTC</div>
          </div>
          <div className="brut-card p-4" data-testid="metric-today-accepted">
            <div className="label-mono flex items-center gap-1">
              <CheckCircle size={11} weight="fill" className="text-emerald-700" />
              Today · accepted
            </div>
            <div className="font-chivo font-black text-3xl mt-1">{today.accepted || 0}</div>
            <div className="text-[10px] text-zinc-500 mt-1">
              Rate: <b>{pct(today.acceptance_rate)}</b>
            </div>
          </div>
          <div className="brut-card p-4" data-testid="metric-today-rejected">
            <div className="label-mono flex items-center gap-1">
              <XCircle size={11} weight="fill" className="text-rose-700" />
              Today · rejected
            </div>
            <div className="font-chivo font-black text-3xl mt-1">{today.rejected || 0}</div>
            <div className="text-[10px] text-zinc-500 mt-1">See breakdown below</div>
          </div>
          <div className="brut-card p-4 bg-black text-white" data-testid="metric-week-earned">
            <div className="label-mono text-zinc-400 flex items-center gap-1">
              <CurrencyDollar size={11} weight="bold" /> This week · earned
            </div>
            <div className="font-chivo font-black text-3xl mt-1">{usd(week.earned)}</div>
            <div className="text-[10px] text-zinc-400 mt-1">
              At your agreed per-state cost
            </div>
          </div>
          <div className="brut-card p-4" data-testid="metric-week-delivered">
            <div className="label-mono">Week · delivered</div>
            <div className="font-chivo font-black text-3xl mt-1">{week.delivered || 0}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Reached the buyer</div>
          </div>
        </div>

        {/* Iter-30.7 — rejection-reason breakdown */}
        <div className="brut-card p-5 mb-8" data-testid="rejected-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <XCircle size={14} weight="fill" className="text-rose-700" />
            <div className="font-chivo font-black uppercase tracking-tighter">
              Why leads were rejected · This week
            </div>
            <div className="label-mono ml-auto">total rejected: {week.rejected}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {REJECT_REASONS.map((r) => (
              <div
                key={r.key}
                className={`border-2 p-3 ${r.color}`}
                data-testid={`reject-reason-${r.key}`}
                title={r.hint}
              >
                <div className="text-[10px] uppercase tracking-widest font-mono">{r.label}</div>
                <div className="font-chivo font-black text-3xl mt-0.5">
                  {weekBreak[r.key] || 0}
                </div>
                <div className="text-[9px] mt-1 opacity-70">today: {todayBreak[r.key] || 0}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
            Hover any tile for what it means. Bring down <b>Invalid</b> by
            filling required PI fields; <b>Duplicates</b> by de-duping your feed;
            <b> Capped</b> is client-side (out of your hands).
          </div>
        </div>

        {/* LIVE vs SANDBOX split */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="metric-live-sandbox">
          <div className="brut-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={16} weight="fill" className="text-emerald-700" />
              <div className="font-chivo font-black uppercase tracking-tighter">Live api key</div>
              <div className="label-mono ml-auto">This week</div>
            </div>
            <div className="font-chivo font-black text-4xl">{week.live || 0}</div>
            <div className="text-xs text-zinc-600 mt-1">
              Leads posted with the LIVE key — dispatched to the buyer&apos;s inbox.
            </div>
          </div>
          <div className="brut-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <Flask size={16} weight="fill" className="text-sky-700" />
              <div className="font-chivo font-black uppercase tracking-tighter">Sandbox api key</div>
              <div className="label-mono ml-auto">This week</div>
            </div>
            <div className="font-chivo font-black text-4xl">{week.sandbox || 0}</div>
            <div className="text-xs text-zinc-600 mt-1">
              Test leads — never delivered to the buyer. Use these to confirm your integration works.
            </div>
          </div>
        </div>

        {/* Iter-66: approved landing pages + pre-filled tracking URLs */}
        <ApprovedLandingPages
          approved={data.affiliate?.approved_landing_pages || []}
          publicSourceId={data.affiliate?.public_source_id}
        />

        <RotatorLinks />

        <VendorMarketplaceStats />

        {/* Iter-32: Traffic Sources — landing-page + sub_id conversion breakdown */}
        <TrafficSources />

        {/* Two-column: recent submissions (left), state earnings (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 brut-card overflow-hidden">
            <div className="px-4 md:px-5 py-3 border-b border-black flex items-center gap-2 bg-white">
              <TrendUp size={14} weight="bold" />
              <div className="font-chivo font-black uppercase tracking-tighter">
                Recent submissions
              </div>
              <div className="label-mono ml-auto">last 50</div>
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black text-[10px] uppercase tracking-widest bg-zinc-100">
                    <th className="text-left py-2 px-4">Submitted</th>
                    <th className="text-left py-2 px-4">State</th>
                    <th className="text-left py-2 px-4">Status</th>
                    <th className="text-left py-2 px-4">Delivery</th>
                    <th className="text-left py-2 px-4">Mode</th>
                    <th className="text-left py-2 px-4">Lead ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent || []).map((r) => (
                    <tr key={r.lead_id} className="border-b border-zinc-200" data-testid={`recent-lead-${r.lead_id}`}>
                      <td className="py-2 px-4 font-mono text-[11px]">{fmtWhen(r.created_at)}</td>
                      <td className="py-2 px-4 font-mono text-[11px]">{r.state || "—"}</td>
                      <td className="py-2 px-4 text-[11px]">
                        <span className={"border px-1.5 py-0.5 uppercase text-[9px] tracking-widest " + (STATUS_STYLE[r.status] || "border-zinc-500 bg-zinc-100 text-zinc-800")}>
                          {r.status || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-4 font-mono text-[10px]">{r.delivery_status || "—"}</td>
                      <td className="py-2 px-4">
                        {r.is_test ? (
                          <span className="border border-sky-700 text-sky-800 bg-sky-50 px-1.5 py-0.5 uppercase text-[9px] tracking-widest">sandbox</span>
                        ) : (
                          <span className="border border-emerald-700 text-emerald-800 bg-emerald-50 px-1.5 py-0.5 uppercase text-[9px] tracking-widest">live</span>
                        )}
                      </td>
                      <td className="py-2 px-4 font-mono text-[10px] text-zinc-500">
                        {r.lead_id.slice(0, 8)}
                      </td>
                    </tr>
                  ))}
                  {!(data.recent || []).length && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center label-mono">
                        No leads submitted this week yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden p-2 flex flex-col gap-2" data-testid="recent-leads-cards">
              {(data.recent || []).map((r) => (
                <div key={r.lead_id} className="border border-black bg-white p-3" data-testid={`recent-lead-card-${r.lead_id}`}>
                  <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                    <div className="font-mono font-bold text-sm">{r.state || "—"}</div>
                    <span className={"border px-1.5 py-0.5 uppercase text-[9px] tracking-widest " + (STATUS_STYLE[r.status] || "border-zinc-500 bg-zinc-100 text-zinc-800")}>{r.status || "—"}</span>
                    <span className="ml-auto">
                      {r.is_test ? (
                        <span className="border border-sky-700 text-sky-800 bg-sky-50 px-1.5 py-0.5 uppercase text-[9px] tracking-widest">sandbox</span>
                      ) : (
                        <span className="border border-emerald-700 text-emerald-800 bg-emerald-50 px-1.5 py-0.5 uppercase text-[9px] tracking-widest">live</span>
                      )}
                    </span>
                  </div>
                  <div className="text-[11px] font-mono text-zinc-600 mt-1">{fmtWhen(r.created_at)} · delivery {r.delivery_status || "—"}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Lead {r.lead_id.slice(0, 8)}</div>
                </div>
              ))}
              {!(data.recent || []).length && (
                <div className="py-8 text-center label-mono">No leads submitted this week yet.</div>
              )}
            </div>
          </div>

          <div className="brut-card overflow-hidden" data-testid="state-earnings">
            <div className="px-4 md:px-5 py-3 border-b border-black flex items-center gap-2 bg-white">
              <div className="font-chivo font-black uppercase tracking-tighter">
                Weekly earnings by state
              </div>
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black text-[10px] uppercase tracking-widest bg-zinc-100">
                    <th className="text-left py-2 px-3">State</th>
                    <th className="text-right py-2 px-3">Accepted</th>
                    <th className="text-right py-2 px-3">Unit</th>
                    <th className="text-right py-2 px-3">Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {stateRows.map((r) => (
                    <tr key={r.state} className="border-b border-zinc-200" data-testid={`vendor-state-${r.state}`}>
                      <td className="py-2 px-3 font-mono font-bold">{r.state}</td>
                      <td className="py-2 px-3 text-right font-mono">{r.count}</td>
                      <td className="py-2 px-3 text-right font-mono">{usd(r.unit_cost)}</td>
                      <td className="py-2 px-3 text-right font-mono font-bold">{usd(r.earnings)}</td>
                    </tr>
                  ))}
                  {!stateRows.length && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center label-mono">
                        No accepted leads this week.
                      </td>
                    </tr>
                  )}
                </tbody>
                {stateRows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-black">
                      <td colSpan={3} className="py-2 px-3 text-right uppercase text-[10px] tracking-widest font-bold">Total this week</td>
                      <td className="py-2 px-3 text-right font-mono font-black bg-black text-white">{usd(week.earned)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden p-2 flex flex-col gap-2" data-testid="vendor-state-cards">
              {stateRows.map((r) => (
                <div key={r.state} className="border border-black bg-white p-3" data-testid={`vendor-state-card-${r.state}`}>
                  <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-200">
                    <div className="font-mono font-bold">{r.state}</div>
                    <span className="ml-auto font-mono font-bold">{usd(r.earnings)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mt-1 font-mono">
                    <div><span className="label-mono">Accepted:</span> {r.count}</div>
                    <div className="text-right"><span className="label-mono">Unit:</span> {usd(r.unit_cost)}</div>
                  </div>
                </div>
              ))}
              {!stateRows.length && (
                <div className="py-8 text-center label-mono">No accepted leads this week.</div>
              )}
              {stateRows.length > 0 && (
                <div className="border-2 border-black bg-black text-white p-3 flex items-center justify-between" data-testid="vendor-state-total-mobile">
                  <span className="uppercase text-[10px] tracking-widest font-bold">Total this week</span>
                  <span className="font-mono font-black">{usd(week.earned)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {unpricedWeek.length > 0 && (
          <div
            className="mt-6 border-2 border-amber-700 bg-amber-50 p-3 text-[11px] text-amber-900"
            data-testid="unpriced-states-warning"
          >
            ⚠ {unpricedWeek.length} state(s) delivered without a cost configured in your
            agreement — <b>{unpricedWeek.join(", ")}</b>. Contact ops to add these to
            your rate card so they show up in your weekly earnings.
          </div>
        )}

        <div className="mt-8 text-xs text-zinc-500 leading-relaxed">
          Read-only view. Earnings shown are what LeadFlow pays you per your agreement
          — you do <b>not</b> see what the lead sells for downstream. If your acceptance
          rate drops or a category on the breakdown spikes, reach out to ops.
        </div>
      </div>
    </div>
  );
}
